import { Link } from 'react-router-dom';
import type { SessionSummary } from '@floorpoll/shared';

/**
 * A poll's sealed runs (#46).
 *
 * A list of dates would answer "when did I present this"; the question the
 * story actually asks is "was tonight's room bigger than last time", so each
 * run carries a bar of its turnout against the best night in the list. The
 * comparison is the point, and it is the one thing the accumulated total can
 * never show.
 *
 * Ink, not tungsten: amber is reserved for a poll that is live right now, and
 * every run in here is over.
 */
export function RunHistory({ runs }: { runs: SessionSummary[] }) {
  // The busiest night sets the scale, so the bars compare runs to each other
  // rather than to a ceiling nobody in this list reached.
  const busiest = runs.reduce((most, run) => Math.max(most, run.participantCount), 0);

  return (
    <ol className="runs">
      {runs.map((run) => (
        <li key={run.sessionId} className="run-row">
          <Link to={`/runs/${run.sessionId}`} className="run-date">
            {formatRunDate(run.endedAt)}
          </Link>
          {/* Hidden from the accessibility tree: the bar is a way to compare
              numbers at a glance, and the counts beside it already say them. */}
          <span className="run-track" aria-hidden="true">
            <span
              className="run-fill"
              // A run nobody attended still gets a visible sliver, so an empty
              // night reads as "nobody came" rather than as a broken row.
              style={{
                width:
                  busiest === 0 ? '0%' : `${Math.max(2, (run.participantCount / busiest) * 100)}%`,
              }}
            />
          </span>
          <span className="run-counts">
            <strong>{run.participantCount}</strong>{' '}
            {run.participantCount === 1 ? 'person' : 'people'} ·{' '}
            {run.voteCount === 1 ? '1 vote' : `${run.voteCount} votes`}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * "14 Aug 2026, 20:00" in the reader's own locale and zone.
 *
 * A run is remembered as an evening, so the time of day earns its place: two
 * runs of the same poll on one conference day are otherwise the same row.
 */
export function formatRunDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
